import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  AlertCircle,
  CircleCheckBig,
  LoaderCircle,
  ShoppingCart,
} from "lucide-react";
import CountdownTimer from "@/components/CountdownTimer.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { setTitle } from "@/hooks/useHeader.ts";
import { Draw } from "@/types/draw.ts";
import { getDraw } from "@/api/getDraw.ts";
import { Link, useParams } from "react-router";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { buyTicket, subscribeTicket } from "@/api/buyTickets.ts";
import { getDrawPrizes } from "@/api/getDrawPrizes.ts";
import { toast } from "sonner";
import TicketCard from "@/components/TicketCard.tsx";
import { Prize } from "@/types/prize.ts";
import PrizeCard from "@/components/PrizeCard.tsx";
import { getErrorResponse } from "@/api/common/handleAxiosError.ts";
import { showErrorToast } from "@/lib/showErrorToast.tsx";
import { updateUser, useUser } from "@/hooks/useUser.ts";
import { useTranslation } from "@/i18n";

interface DrawWithPrize extends Draw {
  prizes: Prize[];
}

const RaffleDetail = () => {
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [draw, setDraw] = useState<DrawWithPrize>();
  const [error, setError] = useState<string>();
  const [purchaseType, setPurchaseType] = useState("onetime");
  const user = useUser();
  const { t } = useTranslation();

  const { id } = useParams();

  const fetchDraw = async () => {
    try {
      const [responseDraw, responsePrize] = await Promise.all([
        await getDraw({ id }),
        await getDrawPrizes({ id }),
      ]);

      setDraw({ ...responseDraw.data, prizes: responsePrize.data });
      setTitle(responseDraw.data.name);
      setPurchaseType(
        responseDraw.data?.lottery?.type === "subscription"
          ? "subscription"
          : "onetime",
      );
    } catch (e) {
      setError(t("raffleDetail.fetchFailed"));
    }
  };

  useEffect(() => {
    setTitle(t("raffleDetail.title"));
    fetchDraw();
  }, []);

  const onBuy = async () => {
    setLoading(true);
    if (purchaseType === "subscription") {
      try {
        const response = await subscribeTicket({
          lotteryId: draw?.lottery?.id,
          count: quantity,
        });
        if ("redirect_url" in response.data) {
          window.location.assign(response.data.redirect_url);
        } else {
          updateUser();
          toast(
            t("raffleDetail.subscribed", {
              count: response.data.ticket_count,
            }),
            {
              duration: 5000,
              icon: <CircleCheckBig className="size-4 text-green-600" />,
            },
          );
        }
      } catch (e: unknown) {
        const errors = getErrorResponse<string[] | unknown>(e);
        if (Array.isArray(errors)) {
          showErrorToast(errors[0] || t("raffleDetail.subscribeFailed"));
        }
      }

      await fetchDraw();
    } else {
      try {
        const response = await buyTicket({ id, count: quantity });
        if ("redirect_url" in response.data) {
          window.location.assign(response.data.redirect_url);
        } else {
          updateUser();
          toast(
            t("raffleDetail.bought", { count: response.data.ticket_count }),
            {
              duration: 5000,
              icon: <CircleCheckBig className="size-4 text-green-600" />,
            },
          );
        }
        await fetchDraw();
      } catch (e: unknown) {
        const errors = getErrorResponse<string[] | unknown>(e);
        if (Array.isArray(errors)) {
          showErrorToast(errors[0] || t("raffleDetail.buyFailed"));
        }
      }
    }
    setLoading(false);
  };

  const incrementQuantity = () => {
    if (quantity < draw!.user_ticket_maximum) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  if (error) {
    return (
      <Alert variant="destructive" className="mx-auto my-4 w-fit">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t("common.somethingWentWrong")}</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!draw) {
    return <LoaderCircle className="mx-auto size-6 animate-spin" />;
  }

  const remainingTickets = draw.user_ticket_maximum - draw.user_ticket_count;

  return (
    <div className="mx-auto flex h-full min-w-[45vw] flex-col lg:max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold">
            {draw.lottery.name}
          </CardTitle>
          <CardDescription>{draw.lottery.description}</CardDescription>
        </CardHeader>

        <CardContent className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="relative overflow-hidden">
            <img
              src={draw.logo_url || "/placeholder.svg"}
              alt={draw.name}
              className="rounded-lg object-cover"
            />
          </div>

          <div className="space-y-6">
            <div>
              <div className="text-muted-foreground text-sm">
                {t("raffleDetail.drawId", { id: draw.id })}
              </div>
              <h3 className="text-2xl font-bold">
                {t("common.price", { amount: draw.ticket_price })}
              </h3>
              {draw.end_date && (
                <div className="mt-3">
                  <CountdownTimer
                    time={
                      draw.status === "scheduled"
                        ? new Date(draw.start_date)
                        : new Date(draw.end_date)
                    }
                    upComing={draw.status === "scheduled"}
                  />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h2 className="text-xl font-bold">{draw.name}</h2>
              <p>{draw.description}</p>
              <p>{draw.draw_cta}</p>
            </div>
            {draw.status === "live" && (
              <>
                {draw.lottery.type === "subscription" && (
                  <div className="space-y-3">
                    <h4 className="font-medium">
                      {t("raffleDetail.purchaseOptions")}
                    </h4>
                    <RadioGroup
                      value={purchaseType}
                      onValueChange={setPurchaseType}
                      className="flex items-center"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="subscription"
                          id="subscription"
                        />
                        <Label htmlFor="subscription">
                          {t("raffleDetail.subscribe")}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="onetime" id="onetime" />
                        <Label htmlFor="onetime">
                          {t("raffleDetail.oneTimePurchase")}
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}

                <div className="flex items-center">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                  >
                    -
                  </Button>
                  <Input
                    type="number"
                    value={quantity}
                    onChange={(e) => {
                      const val = Number.parseInt(e.target.value);
                      if (!isNaN(val) && val > 0 && val <= remainingTickets) {
                        setQuantity(val);
                      }
                    }}
                    className="mx-2 w-48 text-center"
                    min={1}
                    max={remainingTickets}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={incrementQuantity}
                    disabled={quantity >= remainingTickets}
                  >
                    +
                  </Button>
                  <span className="text-muted-foreground m-1 text-xs">
                    {t("raffleDetail.maximumTickets", {
                      count: draw.user_ticket_maximum,
                      remaining: remainingTickets,
                    })}
                  </span>
                </div>

                <div className="flex flex-col gap-3">
                  <Button
                    className="flex-1"
                    onClick={onBuy}
                    disabled={remainingTickets === 0 || loading}
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    {purchaseType === "subscription"
                      ? t("raffleDetail.subscribe")
                      : t("raffleDetail.purchase")}
                  </Button>
                  {purchaseType === "subscription" && (
                    <p className="text-xs">
                      {t("raffleDetail.subscriptionAuthorization")}
                    </p>
                  )}
                  <div className="border-input flex items-baseline justify-between rounded-md border px-4 py-2 text-sm">
                    <div>
                      <span>{t("raffleDetail.yourBalance")} </span>{" "}
                      <span className="font-semibold">
                        {t("common.price", { amount: user!.balance })}
                      </span>
                    </div>

                    <Link to="/wallet/deposit">
                      <Button variant="link" size="content">
                        {t("raffleDetail.increaseBalance")}
                      </Button>
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
        <CardFooter>
          <Tabs defaultValue="tikcets" className="mt-6 w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="tikcets">
                {t("raffleDetail.ticketsTab")}
              </TabsTrigger>
              <TabsTrigger value="prizes">
                {t("raffleDetail.prizesTab")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="tikcets" className="p-4">
              {draw.user_ticket_count ? (
                <div className="flex flex-col gap-2">
                  {draw.tickets.map((ticket) => (
                    <TicketCard ticket={ticket} key={ticket.id} mini />
                  ))}
                </div>
              ) : (
                <div className="mt-4 text-center">
                  {t("raffleDetail.noTickets")}
                </div>
              )}
            </TabsContent>
            <TabsContent value="prizes" className="flex flex-col gap-2 p-4">
              {draw.prizes.map((prize) => (
                <PrizeCard prize={prize} key={prize.id} />
              ))}
            </TabsContent>
          </Tabs>
        </CardFooter>
      </Card>
    </div>
  );
};

export default RaffleDetail;
